import React, { Suspense } from 'react';
import {
  Route,
  BrowserRouter,
  useLocation,
  useParams,
  useMatch,
  useNavigate
} from 'react-router-dom';
import { LastLocationProvider } from 'react-router-dom-last-location';
import { useTranslation } from 'react-i18next';
import LocalizedSwitch from './localized_switch';
import LocalizedRouter from './localized_router';
import BugsnagErrorBoundary from '../../config/bugsnag';
import { applicationRoutes } from '../../config/app_routes';
import { appLinks } from '../../config/const';

import AnalyticsAndCommonLogicComponent from '../analytics';
import GeneralPopup from '../general_popup';
import FxMigrationAlert from '../fx_migration_alert';
import CookiesPopover from '../../../common/cookies_popover';
import Registration from '../registration';
import NotifyMe from '../notify_me';
import MessageToApp from '../message_to_app';
import { ProtectedRoute } from './protected_route';
import { useCurrentPath } from './hooks/useCurrentPath';
import Overlays from './overlays/overlays';
import VoipChatOverlay  from '../voip_chat_overlay';
import ChatOverlay from '../chat_overlay';
import FirebaseMessagingProvider from '../firebase_messaging_provider';
import ZendeskMessagingProvider from '../zendesk_messaging_provider';

const routeComponents = {
  HomePage: require('../home_page').default,
  Advisor: require('../advisor').default,
  GamifiedHub: React.lazy(() => import('../gamified_hub/ui/gamified_hub')),
  SearchResults: require('../search_results').default,
  NoSearchResults: React.lazy(() => import('../no_search_results/no_search_results')),
  MyOrders : React.lazy(() => import('../my_orders')),
  AdvisorOrders : React.lazy(() => import('../advisor_orders')),
  OrderDetails : React.lazy(() => import('../order_details')),
  AddFunds : React.lazy(() => import('../add_funds')),
  PromoCode : React.lazy(() => import('../promo_code')),
  Transcript : React.lazy(() => import('../chat_transcript')),
  Settings : React.lazy(() => import('../settings')),
  Cashback : React.lazy(() => import('../cashback')),
  PaymentSources : React.lazy(() => import('../payment_sources')),
  EmptyPreload : React.lazy(() => import('../empty_preload/empty_preload')),
  SpendLimitsVerifications : React.lazy(() => import('../spend_limits_verification')),
  SignInPage : React.lazy(() => import('../signin_page')),
  PurchaseDetailsForMobileApp : React.lazy(() => import('../purchase_details_for_mobile_app')),
  AdvisorBonusForMobileApp : React.lazy(() => import('../advisor_bonus/mobile_app')),
  Categories : React.lazy(() => import('../categories')),
  NotificationPreferences : React.lazy(() => import('../settings/notification_preferences')),
  BlockedAdvisors: React.lazy(() => import('../settings/blocked_advisors')),
  PhoneEditScreen: React.lazy(() => import('../settings/account_settings/phone_edit_screen_container'))
};

const suspenseComponent = (component) => <Suspense>{component}</Suspense>;
const protectedRoute = (component, user) => <ProtectedRoute user={ user }>{component}</ProtectedRoute>;

const RenderRoutes = ({ user }) => {
  const { t } = useTranslation();
  const location = useLocation();
  const params = useParams();
  const history = useNavigate();
  const currentPath = useCurrentPath();

  const getRoutes = () => {
    const routes = [];
    Object.keys(applicationRoutes).forEach((key) => {
      const match = useMatch(`/:lang?${ t(key) }`);

      const { name, withoutSuspense, isProtected } = applicationRoutes[key];

      const Component = routeComponents[name];
      let child = <Component location={ location } params={ params } match={ match } history={ history } currentPath={ currentPath } />;
      if (!withoutSuspense) child = suspenseComponent(child);
      if (isProtected) child = protectedRoute(child, user);

      routes.push(
        <Route
          key={ key }
          exact
          path={ key }
          element={ (
            <LastLocationProvider>
              {child}
            </LastLocationProvider>
          ) }
        />
      );
    });
    return routes;
  };

  const routes = getRoutes();

  return (
    <LocalizedSwitch>
      {routes.map((r) => r)}
    </LocalizedSwitch>
  );
};

function RoutesComponent({
  state,
  voipState,
  user
}) {
  return (
    <BugsnagErrorBoundary>
      <div suppressHydrationWarning id="layout" style={ { width: '100%', backgroundColor: 'var(--app-background-color)' } }>
        <FirebaseMessagingProvider>
          <ZendeskMessagingProvider>
            <BrowserRouter basename="/">
              <LocalizedRouter>
                <RenderRoutes user={ user } />
              </LocalizedRouter>
              <Overlays />
              {state ? <ChatOverlay /> : null}
              {voipState ? <VoipChatOverlay /> : null}
              <AnalyticsAndCommonLogicComponent />
              <GeneralPopup />
              <FxMigrationAlert />
              <CookiesPopover
                wpccMessageText="cookies_popover.wpcc_message_text"
                wpccPrivacyText="cookies_popover.wpcc_privacy_text"
                wpccPrivacyHref={ appLinks.privacyPolicy }
                wpccBtnText="cookies_popover.wpcc_btn_text"
              />
              <Registration />
              <NotifyMe />
              <MessageToApp />
            </BrowserRouter>
          </ZendeskMessagingProvider>
        </FirebaseMessagingProvider>
      </div>
    </BugsnagErrorBoundary>
  );
}

export default RoutesComponent;
